import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { navLinks, company, LOGO_URL } from '../mock';
import { Button } from './ui/button';

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-[#0B1B33]/95 backdrop-blur-lg border-b border-white/5 shadow-lg'
          : 'bg-transparent'
      }`}
    >
      {/* Top bar */}
      <div className="hidden lg:block bg-[#06111F] text-slate-300 text-xs">
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <span>📍 Head Office: McLean, VA · NY · LA</span>
            <span>Mon–Sat · 8:00am – 8:00pm EST</span>
          </div>
          <div className="flex items-center gap-4">
            <a href={`mailto:${company.email}`} className="hover:text-amber-400 transition">
              {company.email}
            </a>
            <span className="text-slate-600">|</span>
            <a href={`tel:${company.phoneRaw}`} className="hover:text-amber-400 transition">
              {company.phone}
            </a>
          </div>
        </div>
      </div>

      <nav className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group">
          <img
            src={LOGO_URL}
            alt="Core Estimation LLC — Cost Estimation & Quantity Takeoff"
            className="h-20 md:h-28 w-auto object-contain group-hover:scale-[1.04] transition-transform drop-shadow-[0_4px_14px_rgba(201,162,74,0.18)]"
          />
        </Link>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) =>
            link.children ? (
              <div
                key={link.label}
                className="relative"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <NavLink
                  to={link.path}
                  className={({ isActive }) =>
                    `flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition ${
                      isActive ? 'text-amber-400' : 'text-slate-200 hover:text-amber-400'
                    }`
                  }
                >
                  {link.label}
                  <ChevronDown className="w-4 h-4" />
                </NavLink>
                {servicesOpen && (
                  <div className="absolute top-full left-0 pt-3 w-80">
                    <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden">
                      <div className="p-2 max-h-[400px] overflow-auto">
                        {link.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            className="block px-4 py-2.5 rounded-lg text-sm text-slate-700 hover:bg-amber-50 hover:text-[#0B1B33] transition"
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                      <Link
                        to="/services"
                        className="block text-center bg-[#0B1B33] text-white text-sm py-3 hover:bg-[#11264a] transition"
                      >
                        View All Services →
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={link.path}
                to={link.path}
                end={link.path === '/'}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm font-medium transition ${
                    isActive ? 'text-amber-400' : 'text-slate-200 hover:text-amber-400'
                  }`
                }
              >
                {link.label}
              </NavLink>
            )
          )}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <a href={`tel:${company.phoneRaw}`} className="flex items-center gap-2 text-white">
            <div className="w-9 h-9 rounded-full bg-amber-500/10 border border-amber-400/30 flex items-center justify-center">
              <Phone className="w-4 h-4 text-amber-400" />
            </div>
            <div className="text-xs">
              <div className="text-slate-400">Talk to an estimator</div>
              <div className="font-semibold text-sm">{company.phone}</div>
            </div>
          </a>
          <Link to="/contact">
            <Button className="bg-amber-500 hover:bg-amber-600 text-[#0B1B33] font-semibold rounded-lg px-5">
              Get a Free Quote
            </Button>
          </Link>
        </div>

        {/* Mobile menu button */}
        <button
          className="lg:hidden text-white p-2"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-[#0B1B33] border-t border-white/10">
          <div className="px-6 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="block py-3 text-slate-200 hover:text-amber-400 border-b border-white/5"
              >
                {link.label}
              </Link>
            ))}
            <Link to="/contact" className="block pt-4">
              <Button className="w-full bg-amber-500 hover:bg-amber-600 text-[#0B1B33] font-semibold">
                Get a Free Quote
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
